import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { Check, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const PLAN_META = {
  ee_report_one: { title: "Raport unic", badge: "O singură dată", features: ["1 raport PDF premium", "Analiză AI extinsă", "Comparables de piață", "Trend 6 luni"] },
  ee_plan_10_monthly: { title: "Plan 10", badge: "Lunar", features: ["10 rapoarte premium / lună", "Istoric nelimitat", "Export PDF", "Suport email"] },
  ee_plan_20_monthly: { title: "Plan 20", badge: "Lunar · Popular", features: ["20 rapoarte premium / lună", "Istoric nelimitat", "Export PDF", "Suport prioritar"], highlight: true },
  ee_plan_30_monthly: { title: "Plan 30", badge: "Lunar · Pro", features: ["30 rapoarte premium / lună", "Istoric nelimitat", "Export PDF", "Suport prioritar"] },
  ee_plan_annual_1500: { title: "Plan Anual", badge: "1.500 evaluări/an", features: ["1500 rapoarte premium / an", "Economie ~30% vs lunar", "Istoric nelimitat", "Export PDF", "Suport dedicat"] },
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(null);

  useEffect(() => { api.get("/plans").then(r => setPlans(r.data)); }, []);

  const buy = async (lookup_key) => {
    if (!email) return toast.error("Introdu emailul mai întâi");
    setLoading(lookup_key);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url, email });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la checkout");
      setLoading(null);
    }
  };

  const one = plans.find(p => p.lookup_key === "ee_report_one");
  const subs = plans.filter(p => p.period !== "one_time");

  return (
    <main data-testid="pricing-page" className="bg-[#F6F4EE] min-h-screen text-[#0D1F2D]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Planuri</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight mt-3">
          Plătești doar pentru <span className="italic font-medium text-[#0F6E5B]">rapoarte premium</span>.
        </h1>
        <p className="mt-5 text-[#52616B] max-w-xl">Evaluarea rapidă rămâne întotdeauna gratuită. Alege un plan doar dacă vrei rapoarte PDF extinse, comparables și istoric.</p>

        {/* Email input */}
        <div className="mt-10 max-w-xl bg-white border border-[#DEDCCF] p-5">
          <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Emailul pe care primești acces</div>
          <div className="mt-2 flex items-center gap-2 border-b border-[#0D1F2D] pb-1">
            <Mail size={16} />
            <input data-testid="pay-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="numele.tau@email.ro" className="flex-1 bg-transparent focus:outline-none text-lg font-display py-2" />
          </div>
        </div>

        {/* One-time */}
        {one && (
          <div className="mt-12 bg-[#0D1F2D] text-[#F6F4EE] p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#0F6E5B]">{PLAN_META[one.lookup_key].badge}</div>
              <div className="font-display text-3xl font-bold mt-2">{PLAN_META[one.lookup_key].title} · {fmtEUR(one.price_eur)}</div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                {PLAN_META[one.lookup_key].features.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm opacity-90"><Check size={14} className="text-[#0F6E5B]" />{f}</div>
                ))}
              </div>
            </div>
            <button data-testid="buy-ee_report_one" onClick={() => buy("ee_report_one")} disabled={loading === "ee_report_one"} className="bg-[#0F6E5B] hover:bg-[#0A5447] text-white px-7 h-14 rounded-sm font-medium flex items-center gap-2 disabled:opacity-50 shrink-0">
              {loading === "ee_report_one" ? <Loader2 className="animate-spin" size={16} /> : null} Cumpără acum
            </button>
          </div>
        )}

        {/* Subscriptions grid */}
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {subs.map(p => {
            const meta = PLAN_META[p.lookup_key] || {};
            return (
              <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`} className={`relative bg-white border p-6 flex flex-col ${meta.highlight ? "border-[#0F6E5B] border-2" : "border-[#DEDCCF]"}`}>
                {meta.highlight && <div className="absolute -top-3 left-6 bg-[#0F6E5B] text-white mono text-[10px] uppercase tracking-[0.22em] px-2 py-1">Recomandat</div>}
                <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">{meta.badge}</div>
                <div className="font-display text-2xl font-bold mt-1">{meta.title}</div>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-bold">{p.price_eur}€</span>
                  <span className="mono text-xs text-[#52616B]">/{p.period === "year" ? "an" : "lună"}</span>
                </div>
                <div className="mono text-xs text-[#52616B] mt-2">{p.credits} rapoarte premium</div>
                <ul className="mt-6 space-y-2 flex-1">
                  {meta.features?.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm"><Check size={14} className="text-[#0F6E5B] mt-1 shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <button data-testid={`buy-${p.lookup_key}`} onClick={() => buy(p.lookup_key)} disabled={loading === p.lookup_key} className={`mt-6 ${meta.highlight ? "bg-[#0F6E5B] hover:bg-[#0A5447]" : "bg-[#0D1F2D] hover:bg-[#0F6E5B]"} text-white h-12 rounded-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2`}>
                  {loading === p.lookup_key ? <Loader2 className="animate-spin" size={14} /> : null} Alege {meta.title}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-xs text-[#52616B] mono max-w-2xl">
          * Plăți procesate securizat prin Stripe. TVA inclus unde aplicabil. Abonamentele se reînnoiesc automat lunar/anual și pot fi anulate oricând.
          Pentru testare, folosește cardul <span className="text-[#0D1F2D]">4242 4242 4242 4242</span>, orice dată viitoare și orice CVC.
        </div>
      </div>
    </main>
  );
}
